'use client'

import { Button } from '@/ui/atoms/button/Button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { Eye, EyeOff, Loader2, Snowflake } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { ElementType } from 'react'
import {
  type EncryptedSecret,
  getCardPublicKey,
  useCardRevealPayload,
  useFreezeCard,
  useUpdateEncryptedPin,
} from '../hooks/useCardDetails'
import type { UserCard } from '../hooks/useUserCards'

const CARD_REVEAL_MESSAGE_SOURCE = 'hypurr-card-reveal'
const CARD_REVEAL_FRAME_SRC = '/api/card/reveal-frame'
const REVEAL_FRAME_BOOT_TIMEOUT_MS = 15_000
const EyeIcon = Eye as ElementType<{ className?: string }>
const EyeOffIcon = EyeOff as ElementType<{ className?: string }>
const LoaderIcon = Loader2 as ElementType<{ className?: string }>
const SnowflakeIcon = Snowflake as ElementType<{ className?: string }>

type RevealFrameMessage =
  | { source: typeof CARD_REVEAL_MESSAGE_SOURCE; channel: string; type: 'ready' }
  | { source: typeof CARD_REVEAL_MESSAGE_SOURCE; channel: string; type: 'session-created'; sessionId: string }
  | { source: typeof CARD_REVEAL_MESSAGE_SOURCE; channel: string; type: 'rendered' }
  | { source: typeof CARD_REVEAL_MESSAGE_SOURCE; channel: string; type: 'error'; message: string }
  | {
      source: typeof CARD_REVEAL_MESSAGE_SOURCE
      channel: string
      type: 'update-pin'
      sessionId: string
      encryptedPin: EncryptedSecret
    }

interface CardDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  card?: UserCard | null
}

function createRevealChannel(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to reveal card details'
}

function isRevealFrameMessage(value: unknown): value is RevealFrameMessage {
  if (!value || typeof value !== 'object') return false

  const message = value as Partial<RevealFrameMessage>
  return message.source === CARD_REVEAL_MESSAGE_SOURCE && typeof message.channel === 'string'
}

function formatExpiry(month?: string, year?: string): string | null {
  if (!month || !year) return null
  return `${month}/${year}`
}

export function CardDetailsModal({ isOpen, onClose, card: activeCard }: CardDetailsModalProps) {
  const hasActiveCard = !!activeCard
  const activeCardID = activeCard?.cardID
  const [shouldReveal, setShouldReveal] = useState(false)
  const [revealChannel, setRevealChannel] = useState(createRevealChannel)
  const [revealStatus, setRevealStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle')
  const [revealError, setRevealError] = useState<string | null>(null)
  const [hasRevealFrameResponse, setHasRevealFrameResponse] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const { mutateAsync: revealPayload } = useCardRevealPayload(activeCard)
  const { mutateAsync: updateEncryptedPin } = useUpdateEncryptedPin(activeCard)
  const revealPayloadRef = useRef(revealPayload)
  const updateEncryptedPinRef = useRef(updateEncryptedPin)
  const freezeCardMutation = useFreezeCard(activeCard)

  const isCardLocked = activeCard?.status.toLowerCase() === 'locked' || activeCard?.status.toLowerCase() === 'frozen'
  const expiry = formatExpiry(activeCard?.expirationMonth, activeCard?.expirationYear)
  const isDetailsRevealed = revealStatus === 'loaded'
  const isRevealPending = shouldReveal && revealStatus !== 'loaded' && revealStatus !== 'error'
  const cardHeaderLabel = isDetailsRevealed && expiry ? `${activeCard?.type} - ${expiry}` : activeCard?.type

  useEffect(() => {
    if (!isOpen) {
      setShouldReveal(false)
      setRevealStatus('idle')
      setRevealError(null)
      setHasRevealFrameResponse(false)
    }
  }, [isOpen])

  useEffect(() => {
    revealPayloadRef.current = revealPayload
  }, [revealPayload])

  useEffect(() => {
    updateEncryptedPinRef.current = updateEncryptedPin
  }, [updateEncryptedPin])

  useEffect(() => {
    if (!activeCardID) {
      setShouldReveal(false)
      setRevealStatus('idle')
      setRevealError(null)
      setHasRevealFrameResponse(false)
      return
    }

    setShouldReveal(false)
    setRevealStatus('idle')
    setRevealError(null)
    setHasRevealFrameResponse(false)
  }, [activeCardID])

  useEffect(() => {
    if (!shouldReveal || revealStatus !== 'loading' || hasRevealFrameResponse) return

    const timeoutId = window.setTimeout(() => {
      setRevealStatus('error')
      setRevealError('Secure card details failed to load. Please retry.')
    }, REVEAL_FRAME_BOOT_TIMEOUT_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [hasRevealFrameResponse, revealStatus, shouldReveal])

  useEffect(() => {
    if (!shouldReveal || !activeCard) return

    let cancelled = false

    const postToRevealFrame = (message: Record<string, unknown>) => {
      iframeRef.current?.contentWindow?.postMessage(
        {
          source: CARD_REVEAL_MESSAGE_SOURCE,
          channel: revealChannel,
          ...message,
        },
        // The sandboxed frame has an opaque origin, so a literal target origin cannot be used.
        // The random channel and event.source checks bind messages to this iframe instance.
        '*',
      )
    }

    const handleFrameMessage = async (event: MessageEvent<unknown>) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      if (!isRevealFrameMessage(event.data)) return
      if (event.data.channel !== revealChannel) return

      setHasRevealFrameResponse(true)

      if (event.data.type === 'ready') {
        setRevealStatus('loading')

        try {
          const publicKey = await getCardPublicKey()
          if (cancelled) return

          postToRevealFrame({
            type: 'init',
            publicKey,
            display: {
              last4: activeCard.last4,
              type: activeCard.type,
              expirationMonth: activeCard.expirationMonth,
              expirationYear: activeCard.expirationYear,
            },
          })
        } catch (error) {
          if (cancelled) return

          const message = getErrorMessage(error)
          setRevealStatus('error')
          setRevealError(message)
          postToRevealFrame({ type: 'error', message })
        }
      }

      if (event.data.type === 'session-created') {
        try {
          const secrets = await revealPayloadRef.current({ sessionId: event.data.sessionId })
          if (cancelled) return

          postToRevealFrame({
            type: 'secrets',
            secrets,
          })
        } catch (error) {
          if (cancelled) return

          const message = getErrorMessage(error)
          setRevealStatus('error')
          setRevealError(message)
          postToRevealFrame({ type: 'error', message })
        }
      }

      if (event.data.type === 'rendered') {
        setRevealStatus('loaded')
        setRevealError(null)
      }

      if (event.data.type === 'error') {
        setRevealStatus('error')
        setRevealError(event.data.message)
      }

      if (event.data.type === 'update-pin') {
        try {
          await updateEncryptedPinRef.current({
            sessionId: event.data.sessionId,
            encryptedPin: event.data.encryptedPin,
          })
          if (cancelled) return

          postToRevealFrame({ type: 'update-pin-result', ok: true })
        } catch (error) {
          const message = getErrorMessage(error)
          postToRevealFrame({ type: 'update-pin-result', ok: false, message })
        }
      }
    }

    window.addEventListener('message', handleFrameMessage)

    return () => {
      cancelled = true
      window.removeEventListener('message', handleFrameMessage)
    }
  }, [activeCard, revealChannel, shouldReveal])

  const handleShowDetails = () => {
    if (!hasActiveCard || isCardLocked) return

    setRevealChannel(createRevealChannel())
    setRevealError(null)
    setHasRevealFrameResponse(false)
    setRevealStatus('loading')
    setShouldReveal(true)
  }

  const handleHideDetails = () => {
    setShouldReveal(false)
    setRevealStatus('idle')
    setRevealError(null)
    setHasRevealFrameResponse(false)
  }

  const handleFreezeToggle = () => {
    if (isCardLocked) {
      freezeCardMutation.mutate(false)
    } else {
      freezeCardMutation.mutate(true)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" overlayClassName="bg-black">
        <DialogHeader>
          <DialogTitle gradient>Card Details</DialogTitle>
        </DialogHeader>

        {!activeCard && (
          <div className="flex flex-col items-center gap-4 py-8">
            <Typography variant="p" className="text-muted-foreground">
              No card selected
            </Typography>
            <Button onClick={onClose}>Close</Button>
          </div>
        )}

        {activeCard && (
          <div className="flex flex-col gap-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'rounded-full border px-2 py-0.5 font-medium text-xs',
                    isCardLocked
                      ? 'border-yellow-500/30 bg-yellow-500/20 text-yellow-400'
                      : 'border-green-500/30 bg-green-500/20 text-green-400',
                  )}
                >
                  {isCardLocked ? 'FROZEN' : 'ACTIVE'}
                </span>
                <Typography variant="p" className="font-mono text-muted-foreground text-sm">
                  ....{activeCard.last4}
                </Typography>
              </div>
              <Typography variant="p" className="text-muted-foreground text-xs uppercase">
                {cardHeaderLabel}
              </Typography>
            </div>

            <div className="overflow-hidden rounded-lg border border-border">
              {!shouldReveal ? (
                <div className="flex flex-col items-center gap-3 py-10">
                  <EyeIcon className="h-10 w-10 text-muted-foreground/40" />
                  <Typography variant="p" className="text-center text-muted-foreground text-sm">
                    {isCardLocked ? 'Unfreeze card to reveal details' : 'Card details are encrypted'}
                  </Typography>
                  <Button onClick={handleShowDetails} disabled={!hasActiveCard || isCardLocked}>
                    <EyeIcon className="mr-2 h-4 w-4" />
                    Reveal Details
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col">
                  <div className="flex items-center justify-between border-border border-b px-4 py-2">
                    <Typography variant="p" className="font-medium text-sm">
                      Card Details
                    </Typography>
                    <Button variant="ghost" size="sm" onClick={handleHideDetails} className="text-xs">
                      <EyeOffIcon className="mr-1.5 h-3 w-3" />
                      Hide
                    </Button>
                  </div>

                  <div className="relative h-[390px] bg-input">
                    <iframe
                      key={revealChannel}
                      ref={iframeRef}
                      title="Secure card details"
                      src={`${CARD_REVEAL_FRAME_SRC}#channel=${encodeURIComponent(revealChannel)}`}
                      sandbox="allow-scripts"
                      allow="clipboard-write"
                      referrerPolicy="no-referrer"
                      className={cn(
                        'h-full w-full border-0 transition-opacity',
                        isRevealPending ? 'opacity-0' : 'opacity-100',
                      )}
                    />

                    {isRevealPending && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-input p-6 text-center">
                        <LoaderIcon className="h-6 w-6 animate-spin text-primary" />
                        <Typography variant="p" className="text-muted-foreground text-sm">
                          Loading secure card details...
                        </Typography>
                      </div>
                    )}

                    {revealStatus === 'error' && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-input p-6 text-center">
                        <Typography variant="p" className="text-red-400 text-sm">
                          {revealError ?? 'Failed to load card details'}
                        </Typography>
                        <Button size="sm" onClick={handleShowDetails}>
                          Retry
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <Button
              variant="outline"
              onClick={handleFreezeToggle}
              disabled={freezeCardMutation.isPending}
              className={cn(
                'w-full transition-colors',
                isCardLocked
                  ? 'border-green-500/50 text-green-400 hover:bg-green-500/10'
                  : 'border-red-500/50 text-red-400 hover:bg-red-500/10',
              )}
            >
              <SnowflakeIcon className="mr-2 h-4 w-4" />
              {freezeCardMutation.isPending
                ? isCardLocked
                  ? 'Unfreezing...'
                  : 'Freezing...'
                : isCardLocked
                  ? 'Unfreeze Card'
                  : 'Freeze Card'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
